/**
 * Vincent B737 (Eric 2026-02-14) — Progression détaillée par pipeline
 * d'extraction des connaissances.
 *
 * Contexte : `pipeline_status` passe à "complete" AVANT que les 7 pipelines
 * modulaires (Q&R, Scripts, Procédures, Objections, Concepts, Dictionnaire,
 * Synthèse) aient fini — nécessaire pour que `_generate_concepts_impl`
 * accepte de tourner. Sans un tracker visible, Vincent voit la vidéo
 * « Complete » alors que Q&R et Procédures tournent encore 4 minutes → il
 * croit à tort qu'on les a « oubliés ».
 *
 * Cette carte lit `video.knowledge_extraction_progress` et affiche pour
 * chaque pipeline : pending / running / ok (avec count) / failed (avec erreur).
 *
 * Visible uniquement pour les utilisateurs autorisés à modifier/concevoir
 * (canEditKnowledge). Auto-masquée si aucun tracker n'existe (vieux
 * enregistrements pré-B737).
 */
import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle, CircleNotch, Warning, Clock, CaretDown, CaretUp, ArrowClockwise, Prohibit } from "@phosphor-icons/react";
import { confirmDialog } from "../../lib/uiDialogs";

// Bogue 1195 (Éric 2026-06-25) — « Dictionnaire » a quitté le lot
// automatique : il se génère sur clic depuis la Synthèse. Le laisser ici
// afficherait une étape éternellement « en attente ».
const PIPELINE_ORDER = ["Q&R", "Scripts", "Procédures", "Objections", "Concepts", "Synthèse"];
// Tâche 1358 (Eric 2026-06-30) — `PIPELINE_ORDER` sert de CLÉ dans les données
// du serveur : on ne le traduit pas, on traduit seulement l'affichage. Sans
// cette table, le panneau Connaissances restait français en version anglaise.
const PIPELINE_LABELS_EN = {
  "Procédures": "Procedures",
  "Synthèse": "Summary",
};
const _pipelineLabel = (cle, lang) => (lang === "en" ? (PIPELINE_LABELS_EN[cle] || cle) : cle);

// B737 v4 (Eric 2026-02-14) — Formate une durée en ms → "3.2s", "12s", "3m35s".
// Ce format compact évite de charger dayjs uniquement pour ce composant.
const _fmtDuration = (ms) => {
  if (typeof ms !== "number" || ms < 0 || !isFinite(ms)) return null;
  if (ms < 1000) return `${ms}ms`;
  const totalSec = Math.round(ms / 100) / 10; // dixième de seconde
  if (totalSec < 60) return `${totalSec.toFixed(totalSec < 10 ? 1 : 0)}s`;
  const totalWhole = Math.round(ms / 1000);
  const m = Math.floor(totalWhole / 60);
  const s = totalWhole % 60;
  return s === 0 ? `${m}m` : `${m}m${s}s`;
};

// Extrait la durée effective d'un pipeline à partir des timestamps ISO
// écrits par le backend (running → ok|failed). Pour running, calcule live
// contre `now` (paramètre pour permettre un re-render horloge sans refetch).
const _pipelineDuration = (p, now) => {
  if (!p || !p.started_at) return null;
  const start = Date.parse(p.started_at);
  if (Number.isNaN(start)) return null;
  const end = p.finished_at ? Date.parse(p.finished_at) : now;
  if (Number.isNaN(end)) return null;
  return Math.max(0, end - start);
};

const _fmtCount = (label, result) => {
  if (!result) return null;
  const map = {
    "Q&R": result.drafts_created,
    "Scripts": result.scripts_created,
    "Procédures": result.atomic_procedures_count,
    "Objections": result.objections_created,
    "Concepts": result.created,
    "Dictionnaire": result.terms_created,
    "Synthèse": result.synthesis_id ? 1 : 0,
  };
  const n = map[label];
  if (typeof n === "number") return `${n} item${n > 1 ? "s" : ""}`;
  return null;
};

const _statusChip = (status, lang = "fr") => {
  // Tâche 1356 (Eric 2026-06-30) — Ces libellés d'état restaient en français
  // dans la version anglaise du panneau Pipeline.
  const _tr = (fr, en) => (lang === "fr" ? fr : en);
  const map = {
    pending: { icon: Clock, cls: "bg-zinc-100 text-zinc-500 border-zinc-200", label: _tr("En attente", "Pending") },
    // B1119 (Eric 2026-09-08) — État « annulée » : la catégorie garde une
    // trace visible et son bouton de relance, plutôt que de faire comme si
    // rien ne s'était passé (choix d'Eric).
    cancelled: { icon: Prohibit, cls: "bg-zinc-100 text-zinc-600 border-zinc-300", label: _tr("Annulée", "Cancelled") },
    running: { icon: CircleNotch, cls: "bg-blue-50 text-blue-700 border-blue-200 animate-pulse", label: _tr("En cours", "Running"), spin: true },
    ok: { icon: CheckCircle, cls: "bg-teal-50 text-teal-700 border-teal-300", label: _tr("Terminé", "Done") },
    failed: { icon: Warning, cls: "bg-red-50 text-red-700 border-red-300", label: _tr("Échec", "Failed") },
  };
  return map[status] || map.pending;
};

export default function KnowledgeExtractionProgress({ progress, canEdit, lang = "fr", api, videoId, onRetryStarted }) {
  const [open, setOpen] = useState(true);
  const [retryingLabel, setRetryingLabel] = useState(null);
  const [retryingAll, setRetryingAll] = useState(false);
  const [cancelling, setCancelling] = useState(null);

  // B1119 (Eric 2026-09-08) — Annule la génération d'UNE catégorie. La
  // confirmation dit la nuance (choix d'Eric) : couper l'attente n'annule pas
  // un appel déjà facturé, mais toute relance est bien annulée — et c'est là
  // que se trouvait la boucle coûteuse.
  const cancelOne = async (label) => {
    const ok = await confirmDialog(
      lang === "fr"
        ? `Annuler la génération de « ${label} » ?\n\n` +
            "• L'appel déjà envoyé au modèle peut avoir été facturé\n" +
            "• Toute relance de cette catégorie est annulée\n" +
            "• La catégorie passe en « annulée » : vous pourrez la relancer quand vous voulez"
        : `Cancel the generation of "${label}"?\n\n` +
            "• A call already sent to the model may have been billed\n" +
            "• Any retry of this category is cancelled\n" +
            '• The category becomes "cancelled": you can relaunch it whenever you want'
    );
    if (!ok) return;
    setCancelling(label);
    try {
      const { data } = await axios.post(
        `${api}/api/videos/${videoId}/knowledge-jobs/${encodeURIComponent(label)}/cancel`,
        {},
        { withCredentials: true },
      );
      toast.success(data?.note || (lang === "fr" ? `« ${label} » annulée` : `"${label}" cancelled`), { duration: 8000 });
      if (onRetryStarted) await onRetryStarted();
    } catch (e) {
      toast.error(e?.response?.data?.detail || (lang === "fr" ? "Annulation impossible" : "Cancellation failed"));
    } finally {
      setCancelling(null);
    }
  };
  // B737 v4 (Eric 2026-02-14) — Horloge live pour animer la durée des
  // pipelines `running`. On tick chaque seconde SEULEMENT si au moins un
  // pipeline tourne, pour éviter des re-renders inutiles quand tout est
  // terminé.
  const [nowTick, setNowTick] = useState(() => Date.now());
  const hasRunning = progress && progress.pipelines && PIPELINE_ORDER.some(
    (l) => progress.pipelines[l]?.status === "running"
  );
  useEffect(() => {
    if (!hasRunning) return undefined;
    const id = setInterval(() => setNowTick(Date.now()), 1000);
    return () => clearInterval(id);
  }, [hasRunning]);

  // B745 (Eric 2026-02-14) — Polling live du video doc parent quand des
  // pipelines tournent. Sans ça, la card affichait un état figé même après
  // 20 min et Vincent croyait le pipeline mort. On refetch toutes les 5s
  // tant qu'AU MOINS UN pipeline est en `pending` ou `running`. Dès que
  // tout est ok/failed, on arrête pour économiser CPU/réseau.
  const hasIncomplete = progress && progress.pipelines && PIPELINE_ORDER.some(
    (l) => ["pending", "running"].includes(progress.pipelines[l]?.status)
  );
  // B745 v2 (Eric 2026-02-14) — Freshness indicator « 🔄 il y a Xs » pour
  // prouver visuellement que le rafraîchissement live tourne bien.
  const [lastPollAt, setLastPollAt] = useState(null);
  // Bogue 1218 (Eric 2026-06-26) — `onRetryStarted` venait du parent sous
  // forme de fonction anonyme : chaque rendu en créait une nouvelle, l'effet
  // ci-dessous se remontait, son premier tick (immédiat) rechargeait le
  // document vidéo, ce qui provoquait un rendu… en boucle. La page clignotait
  // et le serveur recevait une rafale continue. On garde le rappel dans une
  // ref : l'effet ne dépend plus que de « reste-t-il un pipeline en cours ».
  const onRefreshRef = useRef(onRetryStarted);
  useEffect(() => { onRefreshRef.current = onRetryStarted; }, [onRetryStarted]);
  useEffect(() => {
    if (!hasIncomplete) return undefined;
    // On réutilise le callback `onRetryStarted` du parent (VideoDetailPage)
    // qui déclenche loadVideoData + startPolling. Sémantique : « demande un
    // refresh du video doc ». Passe la sentinelle `__poll__` pour
    // distinguer d'un retry manuel.
    const tick = () => {
      if (typeof onRefreshRef.current !== "function") return;
      onRefreshRef.current("__poll__");
      setLastPollAt(Date.now());
    };
    tick(); // 1er tick immédiat pour marquer le début du polling
    const id = setInterval(tick, 5000);
    return () => clearInterval(id);
  }, [hasIncomplete]);
  // Reset lastPollAt quand le polling s'arrête (transition vers all-done)
  useEffect(() => {
    if (!hasIncomplete) setLastPollAt(null);
  }, [hasIncomplete]);

  // B737 v5 (Eric 2026-02-14) — Slow Pipeline Alert. Récupère les moyennes
  // historiques par pipeline pour l'org courante et flag les runs qui
  // dépassent 2× la moyenne (early signal de régression OpenAI).
  // Seuil : `duration_ms > 2 * avg_ms` ET `avg_count >= 3` (évite les
  // fausses alertes sur trop peu d'échantillons).
  const [averages, setAverages] = useState(null);
  useEffect(() => {
    if (!api || !canEdit) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${api}/api/knowledge-extraction/averages`, { withCredentials: true });
        if (!cancelled) setAverages(data?.averages || null);
      } catch (_) { /* silencieux — pas de warning affiché si l'endpoint échoue */ }
    })();
    return () => { cancelled = true; };
  }, [api, canEdit, videoId]);
  const _isSlow = (label, durationMs) => {
    if (!averages || typeof durationMs !== "number") return false;
    const a = averages[label];
    if (!a || !a.avg_ms || (a.count || 0) < 3) return false;
    return durationMs > 2 * a.avg_ms;
  };
  // Ne rend rien si l'utilisateur n'a pas les droits ou si le tracker n'existe pas encore
  // (vieilles vidéos pré-B737).
  if (!canEdit) return null;
  if (!progress || !progress.pipelines) return null;

  const pipelines = progress.pipelines || {};
  const completedAt = progress.completed_at;
  // Tâche 1481 (Eric) — Les compteurs sont RECALCULÉS ici, et non lus dans
  // l'enregistrement. Le Dictionnaire a quitté le lot automatique (tâche
  // 1195) mais 32 suivis en base portent encore son étape : ils annonçaient
  // « Terminée — 6/7 pipelines réussis » alors que les SIX étapes lancées
  // avaient toutes réussi, et Eric cherchait un échec qui n'existait pas.
  // Recalculer corrige aussi tous les anciens enregistrements sans migration.
  // Une étape annulée ne compte ni comme succès ni comme total : elle n'a
  // jamais été tentée.
  const suivies = PIPELINE_ORDER.filter(
    (l) => pipelines[l] && pipelines[l].status !== "cancelled");
  const totalCount = suivies.length || PIPELINE_ORDER.length;
  const successCount = suivies.filter((l) => pipelines[l].status === "ok").length;
  // « Terminée » ne doit plus s'afficher tant qu'une étape tourne ou attend :
  // le serveur écrit `completed_at` avant que tout soit retombé, et la carte
  // affirmait « Terminée » avec Q&R et Procédures encore en cours.
  const resteDuTravail = suivies.some(
    (l) => pipelines[l].status === "running" || pipelines[l].status === "pending");
  const isAllDone = !!completedAt && !resteDuTravail;

  const handleRetry = async (label) => {
    if (!api || !videoId) {
      toast.error(lang === "fr" ? "Configuration manquante pour le retry." : "Missing config for retry.");
      return;
    }
    setRetryingLabel(label);
    const tid = toast.loading(
      lang === "fr" ? `🔁 Relance du pipeline « ${label} »…` : `🔁 Retrying pipeline "${label}"…`,
      { duration: Infinity }
    );
    try {
      await axios.post(
        `${api}/api/videos/${videoId}/retry-knowledge-pipeline/${encodeURIComponent(label)}`,
        {},
        { withCredentials: true, timeout: 30000 }
      );
      toast.success(
        lang === "fr"
          ? `🔁 Pipeline « ${label} » relancé. La carte se mettra à jour automatiquement.`
          : `🔁 Pipeline "${label}" restarted. Card will refresh automatically.`,
        { id: tid, duration: 8000 }
      );
      onRetryStarted?.(label);
    } catch (e) {
      const detail = e.response?.data?.detail || e.message;
      toast.error(
        lang === "fr" ? `❌ Retry échoué : ${detail}` : `❌ Retry failed: ${detail}`,
        { id: tid, duration: 10000 }
      );
    } finally {
      setRetryingLabel(null);
    }
  };

  // Compte statuts en direct (avant completed_at, pour la barre de progression)
  const stats = { ok: 0, failed: 0, running: 0, pending: 0 };
  PIPELINE_ORDER.forEach((label) => {
    const s = pipelines[label]?.status || "pending";
    stats[s] = (stats[s] || 0) + 1;
  });

  // B737 v3 (Eric 2026-02-14) — Retry All Failed. Récupère la liste des
  // pipelines en `failed` et les relance en parallèle via l'endpoint per-pipeline
  // (Promise.allSettled pour ne pas casser sur un 500 isolé). Le tracker
  // remonte automatiquement chaque état individuel — pas besoin d'endpoint
  // « batch » côté backend.
  const failedLabels = PIPELINE_ORDER.filter((l) => pipelines[l]?.status === "failed");
  const handleRetryAllFailed = async () => {
    if (!api || !videoId || failedLabels.length === 0) return;
    setRetryingAll(true);
    const tid = toast.loading(
      lang === "fr"
        ? `🔁 Relance de ${failedLabels.length} pipeline${failedLabels.length > 1 ? "s" : ""} en échec…`
        : `🔁 Retrying ${failedLabels.length} failed pipeline${failedLabels.length > 1 ? "s" : ""}…`,
      { duration: Infinity }
    );
    const results = await Promise.allSettled(
      failedLabels.map((label) =>
        axios.post(
          `${api}/api/videos/${videoId}/retry-knowledge-pipeline/${encodeURIComponent(label)}`,
          {},
          { withCredentials: true, timeout: 30000 }
        )
      )
    );
    const okCount = results.filter((r) => r.status === "fulfilled").length;
    const errCount = results.length - okCount;
    if (errCount === 0) {
      toast.success(
        lang === "fr"
          ? `🔁 ${okCount} pipeline${okCount > 1 ? "s" : ""} relancé${okCount > 1 ? "s" : ""}. La carte se mettra à jour automatiquement.`
          : `🔁 ${okCount} pipeline${okCount > 1 ? "s" : ""} restarted. Card will refresh automatically.`,
        { id: tid, duration: 8000 }
      );
    } else {
      toast.error(
        lang === "fr"
          ? `⚠️ ${okCount} relancé${okCount > 1 ? "s" : ""}, ${errCount} échec${errCount > 1 ? "s" : ""}. Voir les lignes individuelles pour retry manuel.`
          : `⚠️ ${okCount} restarted, ${errCount} failed. See individual rows for manual retry.`,
        { id: tid, duration: 10000 }
      );
    }
    onRetryStarted?.("__batch__");
    setRetryingAll(false);
  };

  return (
    <div
      className="my-3 rounded-lg border border-violet-200 bg-violet-50/40 p-3 text-sm"
      data-testid="knowledge-extraction-progress-card"
    >
      <div className="flex items-center justify-between gap-2">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="flex-1 flex items-center justify-between gap-2 text-left"
          data-testid="knowledge-extraction-progress-toggle"
        >
          <div className="inline-flex items-center gap-2 text-violet-900 font-semibold text-xs uppercase tracking-wider">
            {isAllDone ? <CheckCircle size={14} weight="fill" /> : <CircleNotch size={14} className="animate-spin" />}
            {lang === "fr" ? "Génération des connaissances" : "Knowledge extraction"}
            <span className="text-[11px] normal-case font-normal text-violet-700" data-testid="kep-summary-line">
              {isAllDone
                ? (lang === "fr"
                    ? `Terminée — ${successCount}/${totalCount} pipelines réussis`
                      + (failedLabels.length ? ` · échec : ${failedLabels.join(", ")}` : "")
                    : `Complete — ${successCount}/${totalCount} pipelines succeeded`
                      + (failedLabels.length ? ` · failed: ${failedLabels.join(", ")}` : ""))
                : (lang === "fr"
                    ? `${stats.ok}/${totalCount} terminés · ${stats.running} en cours · ${stats.pending} en attente`
                    : `${stats.ok}/${totalCount} done · ${stats.running} running · ${stats.pending} pending`)}
            </span>
            {/* B745 v2 (Eric 2026-02-14) — Freshness indicator : prouve
                visuellement que le polling live tourne. Affiché uniquement
                pendant que hasIncomplete=true ET après le 1er tick. */}
            {hasIncomplete && lastPollAt && (() => {
              const ageSec = Math.max(0, Math.round((nowTick - lastPollAt) / 1000));
              return (
                <span
                  className="text-[10px] normal-case font-normal text-violet-500 inline-flex items-center gap-0.5 tabular-nums"
                  data-testid="kep-poll-freshness"
                  data-age-seconds={ageSec}
                  title={lang === "fr"
                    ? "Le tableau se rafraîchit automatiquement toutes les 5 s tant que des pipelines tournent."
                    : "Progress table auto-refreshes every 5s while pipelines are running."}
                >
                  <span aria-hidden="true">🔄</span>
                  {lang === "fr" ? `il y a ${ageSec}s` : `${ageSec}s ago`}
                </span>
              );
            })()}
          </div>
          {open ? <CaretUp size={14} className="text-violet-600" /> : <CaretDown size={14} className="text-violet-600" />}
        </button>
        {/* B737 v3 (Eric 2026-02-14) — Bouton « Relancer tous les échecs » —
            visible uniquement si au moins un pipeline est en `failed`.
            Lance les retries en parallèle via Promise.allSettled. */}
        {failedLabels.length > 0 && (
          <button
            type="button"
            onClick={handleRetryAllFailed}
            disabled={retryingAll}
            className="flex-shrink-0 inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-1 rounded bg-red-600 hover:bg-red-700 text-white disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
            data-testid="kep-retry-all-failed-btn"
            title={lang === "fr"
              ? `Relance en parallèle les ${failedLabels.length} pipeline${failedLabels.length > 1 ? "s" : ""} en échec : ${failedLabels.join(", ")}`
              : `Retry the ${failedLabels.length} failed pipeline${failedLabels.length > 1 ? "s" : ""} in parallel: ${failedLabels.join(", ")}`}
          >
            <ArrowClockwise size={11} weight="bold" className={retryingAll ? "animate-spin" : ""} />
            {retryingAll
              ? (lang === "fr" ? "Relance…" : "Retrying…")
              : (lang === "fr"
                  ? `Relancer tous les échecs (${failedLabels.length})`
                  : `Retry all failed (${failedLabels.length})`)}
          </button>
        )}
      </div>

      {open && (
        <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-1.5" data-testid="knowledge-extraction-progress-list">
          {PIPELINE_ORDER.map((label) => {
            const p = pipelines[label] || { status: "pending" };
            const chip = _statusChip(p.status, lang);
            const Icon = chip.icon;
            const count = _fmtCount(label, p.result);
            const durationMs = _pipelineDuration(p, nowTick);
            const durationTxt = _fmtDuration(durationMs);
            return (
              <div
                key={label}
                className={`flex items-center gap-2 rounded border ${chip.cls} px-2 py-1`}
                data-testid={`kep-row-${label}`}
              >
                <Icon size={12} className={chip.spin ? "animate-spin flex-shrink-0" : "flex-shrink-0"} weight={p.status === "ok" ? "fill" : "regular"} />
                <span className="font-semibold text-[12px] flex-shrink-0">{_pipelineLabel(label, lang)}</span>
                <span className="text-[11px] opacity-70 flex-1 truncate">
                  {p.status === "ok" && count && <>· {count}</>}
                  {p.status === "failed" && p.error && (
                    <span title={p.error}>· {(p.error || "").slice(0, 50)}{(p.error || "").length > 50 && "…"}</span>
                  )}
                  {p.status === "running" && (lang === "fr" ? "· en cours…" : "· running…")}
                  {p.status === "pending" && (lang === "fr" ? "· en attente" : "· pending")}
                  {p.status === "cancelled" && (lang === "fr" ? "· annulée" : "· cancelled")}
                </span>
                {/* B1119 (Eric 2026-09-08) — Trace de l'annulation conservée
                    même après une relance (choix d'Eric) : sinon personne ne
                    peut expliquer pourquoi cette catégorie a été regénérée
                    après les six autres. */}
                {p.cancelled_at && (
                  <span
                    className="flex-shrink-0 text-[10px] text-zinc-500 cursor-help"
                    data-testid={`kep-cancel-trace-${label}`}
                    title={lang === "fr"
                      ? `Annulée le ${new Date(p.cancelled_at).toLocaleString("fr-CA")}${p.cancelled_by ? ` par ${p.cancelled_by}` : ""}`
                      : `Cancelled on ${new Date(p.cancelled_at).toLocaleString("en-CA")}${p.cancelled_by ? ` by ${p.cancelled_by}` : ""}`}
                  >
                    <Prohibit size={11} weight="bold" aria-hidden="true" />
                  </span>
                )}
                {/* B737 v4 (Eric 2026-02-14) — Durée du pipeline. Live pour
                    running (tick chaque seconde), figée pour ok/failed
                    (finished_at - started_at). Aide Vincent à repérer les
                    régressions OpenAI (ex: Q&R qui passe de 3m à 8m). */}
                {/* B1119 (Eric 2026-09-08) — Annuler CETTE catégorie. Avant,
                    la seule échappatoire à une génération qui boucle était
                    d'arrêter tout le pipeline. */}
                {canEdit && p.status === "running" && (
                  <button
                    type="button"
                    onClick={() => cancelOne(label)}
                    disabled={cancelling === label}
                    className="flex-shrink-0 text-[10px] font-semibold px-1.5 py-0.5 rounded border border-red-300 bg-white text-red-700 hover:bg-red-50 disabled:opacity-50"
                    data-testid={`kep-cancel-${label}`}
                    title={lang === "fr"
                      ? "Annuler la génération de cette catégorie (l'appel déjà envoyé au modèle peut avoir été facturé ; toute relance est annulée)"
                      : "Cancel this category's generation (a call already sent to the model may have been billed; any retry is cancelled)"}
                  >
                    {cancelling === label ? "…" : (lang === "fr" ? "Annuler" : "Cancel")}
                  </button>
                )}
                {durationTxt && (
                  <span
                    className={`flex-shrink-0 text-[10px] tabular-nums font-mono opacity-70 ${_isSlow(label, durationMs) ? "text-orange-700 font-bold opacity-100" : ""}`}
                    data-testid={`kep-duration-${label}`}
                    title={p.status === "running"
                      ? (lang === "fr" ? "Durée écoulée depuis le démarrage" : "Elapsed time since start")
                      : (lang === "fr" ? "Durée totale du pipeline" : "Total pipeline duration")}
                  >
                    {durationTxt}
                  </span>
                )}
                {/* B737 v5 (Eric 2026-02-14) — Slow Pipeline Alert.
                    Warning orange dès qu'un run dépasse 2× la moyenne
                    historique de l'org sur ce même pipeline (min 3 samples). */}
                {_isSlow(label, durationMs) && averages?.[label]?.avg_ms && (
                  <Warning
                    size={12}
                    weight="fill"
                    className="flex-shrink-0 text-orange-500"
                    data-testid={`kep-slow-warning-${label}`}
                    title={lang === "fr"
                      ? `⚠️ Ce pipeline tourne plus de 2× la moyenne (${_fmtDuration(Math.round(averages[label].avg_ms))} sur ${averages[label].count} runs). Vérifie si OpenAI/GPT-5.2 est dégradé.`
                      : `⚠️ Pipeline is running > 2× the average (${_fmtDuration(Math.round(averages[label].avg_ms))} across ${averages[label].count} runs). Check if OpenAI/GPT-5.2 is degraded.`}
                  />
                )}
                {/* B737 (Eric 2026-02-14) — Bouton Retry : disponible pour les
                    lignes `failed` (récupération) ET `ok` (regénérer si
                    Vincent veut re-tenter avec un meilleur contexte). Caché
                    si `running` ou `pending`. Désactivé pendant le clic. */}
                {/* B1119 (Eric 2026-09-08) — Une catégorie annulée doit
                    pouvoir être relancée, sinon l'annulation devient une
                    impasse : la confirmation la promet explicitement. */}
                {(p.status === "failed" || p.status === "ok" || p.status === "cancelled") && (
                  <button
                    type="button"
                    onClick={() => handleRetry(label)}
                    disabled={retryingLabel === label}
                    className="flex-shrink-0 inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded border border-current opacity-80 hover:opacity-100 disabled:opacity-40 disabled:cursor-not-allowed"
                    data-testid={`kep-retry-${label}`}
                    title={p.status === "failed"
                      ? (lang === "fr" ? `Relancer uniquement le pipeline « ${label} »` : `Retry only pipeline "${label}"`)
                      : p.status === "cancelled"
                        ? (lang === "fr" ? `Relancer « ${label} » après l'annulation (la trace de l'annulation est conservée)` : `Restart "${label}" after cancellation (the cancellation trace is kept)`)
                        : (lang === "fr" ? `Régénérer « ${label} » (le contenu actuel sera conservé, les nouveaux items s'ajouteront)` : `Regenerate "${label}" (current content kept, new items added)`)}
                  >
                    <ArrowClockwise size={10} weight="bold" className={retryingLabel === label ? "animate-spin" : ""} />
                    {retryingLabel === label
                      ? (lang === "fr" ? "…" : "…")
                      : (p.status === "failed"
                          ? (lang === "fr" ? "Réessayer" : "Retry")
                          : p.status === "cancelled"
                            ? (lang === "fr" ? "Relancer" : "Restart")
                            : (lang === "fr" ? "Régénérer" : "Regen"))}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
